import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import SaleOverviewChartCard from "./SaleOverviewChartCard";
import TotalSaleGroupedBarCard from "./TotalSaleGroupedBarCard";
import TopProductsChartCard from "./TopProductsChartCard";
import SaleByCustomerCategoryChartCard from "./SaleByCustomerCategoryChartCard";
import SegmentChartCard from "./SegmentChartCard";
import CustomerTrendChartCard from "./CustomerTrendChartCard";
import TopCustomersChartCard from "./TopCustomersChartCard";
import CustomerGroupChartCard from "./CustomerGroupChartCard";
import AgeAndGenderChartCard from "./AgeAndGenderChartCard";
import SalespersonPerformanceChartCard from "./SalespersonPerformanceChartCard";
import PaymentMethodChartCard from "./PaymentMethodChartCard";
import CustomOrderChartCard from "./CustomOrderChartCard";
import RepairOrderChartCard from "./RepairOrderChartCard";
import RefundChartCard from "./RefundChartCard";
import ReservedChartCard from "./ReservedChartCard";
import ExchangeChartCard from "./ExchangeChartCard";
import WishlistChartCard from "./WishlistChartCard";
import CreditNoteChartCard from "./CreditNoteChartCard";
import DebitNoteChartCard from "./DebitNoteChartCard";
import CashRegisterExpectedActualChartCard from "./CashRegisterExpectedActualChartCard";
import DepositChartCard from "./DepositChartCard";
import DashboardChartCardSkeleton from "./DashboardChartCardSkeleton";
import {
  DASHBOARD_CHART_GRAPH_IDS,
  DASHBOARD_CHART_LOAD_KEYS,
  mergeAgeAndGenderModel,
  mergeCreditNoteModel,
  mergeDebitNoteModel,
  mergeDepositModel,
  mergeCashRegisterExpectedActualModel,
  mergeCustomOrderModel,
  mergeRepairOrderModel,
  mergeRefundModel,
  mergeReservedModel,
  mergeExchangeModel,
  mergeWishlistModel,
  mergeCustomerGroupModel,
  mergeCustomerTrendModel,
  mergePaymentMethodModel,
  mergeSaleByCustomerCategoryModel,
  mergeSalespersonPerformanceModel,
  mergeTotalSaleGroupedModel,
  saleOverviewModelFromDashboardRange,
} from "./dashboardChartsModel";
import "./DashboardChartsArea.scss";

/**
 * Build 2-column rows from an ordered list of chart slots (no placeholders).
 * @template {{id: string, node: React.ReactNode}} T
 * @param {T[]} items
 * @returns {T[][]}
 */
function chunkIntoPairs(items) {
  const rows = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
}

/**
 * @param {{id: string}[]} pair
 * @returns {boolean}
 */
function isCreditDebitPairRow(pair) {
  if (pair.length < 2) return false;
  const ids = new Set(pair.map((p) => p.id));
  return ids.has(DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE) && ids.has(DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE);
}

/**
 * First-row dashboard charts. Visibility follows `visibleIds` (graph picker).
 *
 * @param {object} props
 * @param {Set<string> | null | undefined} props.visibleIds
 * @param {Date | null} [props.startDate]
 * @param {Date | null} [props.endDate]
 * @param {Partial<import("./dashboardChartsModel.js").SaleOverviewChartModel> | null} [props.saleOverviewPartial]
 * @param {Partial<import("./dashboardChartsModel.js").TotalSaleGroupedModel> | null} [props.totalSalePartial]
 * @param {import("./dashboardChartsModel.js").SegmentGraphModel | null} [props.segmentGraphPartial] — mapped `segment-graph` API
 * @param {Partial<import("./dashboardChartsModel.js").SaleByCustomerCategoryModel> | null} [props.saleByCustomerCategoryPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomerTrendModel> | null} [props.customerTrendPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomerGroupModel> | null} [props.customerGroupPartial]
 * @param {Partial<import("./dashboardChartsModel.js").AgeAndGenderModel> | null} [props.ageAndGenderPartial]
 * @param {Partial<import("./dashboardChartsModel.js").SalespersonPerformanceModel> | null} [props.salespersonPerformancePartial]
 * @param {Partial<import("./dashboardChartsModel.js").PaymentMethodModel> | null} [props.paymentMethodPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CreditNoteChartModel> | null} [props.creditNotePartial]
 * @param {Partial<import("./dashboardChartsModel.js").DebitNoteChartModel> | null} [props.debitNotePartial]
 * @param {Partial<import("./dashboardChartsModel.js").DepositChartModel> | null} [props.depositPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CashRegisterExpectedActualModel> | null} [props.cashRegisterPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.customOrderPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.repairOrderPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.refundPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.reservedPartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.exchangePartial]
 * @param {Partial<import("./dashboardChartsModel.js").CustomOrderChartModel> | null} [props.wishlistPartial]
 * @param {(groupBy: 'amount' | 'quantity') => void} [props.onTotalSaleGroupByChange]
 * @param {(graphId: string) => void} [props.onRemoveInlineGraph]
 * @param {Record<string, boolean>} [props.chartLoading] — per-endpoint flags from PosDashboard (true = show skeleton for that chart)
 */
function DashboardChartsArea({
  visibleIds,
  startDate,
  endDate,
  chartLoading: chartLoadingProp,
  saleOverviewPartial,
  totalSalePartial,
  segmentGraphPartial,
  saleByCustomerCategoryPartial,
  customerTrendPartial,
  customerGroupPartial,
  ageAndGenderPartial,
  salespersonPerformancePartial,
  paymentMethodPartial,
  creditNotePartial,
  debitNotePartial,
  depositPartial,
  customOrderPartial,
  cashRegisterPartial,
  repairOrderPartial,
  refundPartial,
  reservedPartial,
  exchangePartial,
  wishlistPartial,
  onTotalSaleGroupByChange,
  onRemoveInlineGraph,
}) {
  const chartLoadingMap = chartLoadingProp && typeof chartLoadingProp === "object" ? chartLoadingProp : {};
  const isChartLoading = (key) => Boolean(chartLoadingMap[key]);
  const segmentSlotLoading =
    isChartLoading(DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE) || isChartLoading(DASHBOARD_CHART_LOAD_KEYS.SEGMENT_GRAPH);
  const customerGraphsLoading = isChartLoading(DASHBOARD_CHART_LOAD_KEYS.CUSTOMER_GRAPHS);

  const mergedBaseTotal = useMemo(() => mergeTotalSaleGroupedModel(totalSalePartial ?? null), [totalSalePartial]);
  const [groupByUi, setGroupByUi] = useState(() => mergedBaseTotal.groupBy ?? "amount");

  useEffect(() => {
    const next = mergedBaseTotal.groupBy ?? "amount";
    setGroupByUi(next);
  }, [mergedBaseTotal.groupBy]);

  const saleModel = useMemo(
    () => saleOverviewModelFromDashboardRange(startDate, endDate, saleOverviewPartial ?? null),
    [saleOverviewPartial, startDate, endDate],
  );
  const totalModel = useMemo(
    () => ({
      ...mergedBaseTotal,
      groupBy: groupByUi ?? "amount",
    }),
    [mergedBaseTotal, groupByUi],
  );
  const saleByCustomerCategoryModel = useMemo(
    () => mergeSaleByCustomerCategoryModel(saleByCustomerCategoryPartial ?? null),
    [saleByCustomerCategoryPartial],
  );
  const customerTrendModel = useMemo(
    () => mergeCustomerTrendModel(customerTrendPartial ?? null),
    [customerTrendPartial],
  );
  const customerGroupModel = useMemo(() => mergeCustomerGroupModel(customerGroupPartial ?? null), [customerGroupPartial]);
  const ageAndGenderModel = useMemo(() => mergeAgeAndGenderModel(ageAndGenderPartial ?? null), [ageAndGenderPartial]);
  const salespersonPerformanceModel = useMemo(
    () => mergeSalespersonPerformanceModel(salespersonPerformancePartial ?? null),
    [salespersonPerformancePartial],
  );
  const paymentMethodModel = useMemo(() => mergePaymentMethodModel(paymentMethodPartial ?? null), [paymentMethodPartial]);
  const customOrderModel = useMemo(() => mergeCustomOrderModel(customOrderPartial ?? null), [customOrderPartial]);
  const repairOrderModel = useMemo(() => mergeRepairOrderModel(repairOrderPartial ?? null), [repairOrderPartial]);
  const refundModel = useMemo(() => mergeRefundModel(refundPartial ?? null), [refundPartial]);
  const reservedModel = useMemo(() => mergeReservedModel(reservedPartial ?? null), [reservedPartial]);
  const exchangeModel = useMemo(() => mergeExchangeModel(exchangePartial ?? null), [exchangePartial]);
  const wishlistModel = useMemo(() => mergeWishlistModel(wishlistPartial ?? null), [wishlistPartial]);
  const creditNoteModel = useMemo(() => mergeCreditNoteModel(creditNotePartial ?? null), [creditNotePartial]);
  const debitNoteModel = useMemo(() => mergeDebitNoteModel(debitNotePartial ?? null), [debitNotePartial]);
  const depositModel = useMemo(() => mergeDepositModel(depositPartial ?? null), [depositPartial]);
  const cashRegisterModel = useMemo(
    () => mergeCashRegisterExpectedActualModel(cashRegisterPartial ?? null),
    [cashRegisterPartial],
  );

  const handleGroupByChange = useCallback(
    (value) => {
      const next = value === "quantity" ? "quantity" : "amount";
      setGroupByUi(next);
      if (typeof onTotalSaleGroupByChange === "function") {
        onTotalSaleGroupByChange(next);
      }
    },
    [onTotalSaleGroupByChange],
  );

  const showSale =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW));
  const showTotal =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE));
  const showTopProduct =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.TOP_PRODUCT));
  const showSegment =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.ITEM_COLLECTION_SEGMENT));
  const showCustomerCategory =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY));
  const showCustomerTrend =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.CUSTOMER_TREND));
  const showTopCustomer =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.TOP_CUSTOMER));
  const showCustomerGroup =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.CUSTOMER_GROUP));
  const showAgeAndGender =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.AGE_AND_GENDER));
  const showSalespersonPerformance =
    visibleIds == null ||
    (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE));
  const showPaymentMethod =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD));
  const showCustomOrder =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER));
  const showRepairOrder =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER));
  const showCreditNote =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE));
  const showDebitNote =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE));
  const showRefund =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.REFUND));
  const showReserved =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.RESERVE));
  const showExchange =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.EXCHANGE));
  const showWishlist =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.WISHLIST));
  const showDeposit =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.DEPOSIT));
  const showCashRegister =
    visibleIds == null || (typeof visibleIds.has === "function" && visibleIds.has(DASHBOARD_CHART_GRAPH_IDS.CASH_REGISTER));

  const row3RightSlotCount = (showCustomerGroup ? 1 : 0) + (showAgeAndGender ? 1 : 0);

  const flowSlotsBeforeRow3 = useMemo(() => {
    /** @type {{ id: string; node: React.ReactNode }[]} */
    const items = [];
    if (showSale) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <SaleOverviewChartCard
            model={saleModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW)}
          />
        ),
      });
    }
    if (showTotal) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <TotalSaleGroupedBarCard
            model={totalModel}
            onGroupByChange={handleGroupByChange}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE)}
          />
        ),
      });
    }
    if (showTopProduct) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.TOP_PRODUCT,
        node: (
          <TopProductsChartCard
            startDate={startDate}
            endDate={endDate}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.TOP_PRODUCT)}
          />
        ),
      });
    }
    if (showSegment) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.ITEM_COLLECTION_SEGMENT,
        node: segmentSlotLoading ? (
          <DashboardChartCardSkeleton />
        ) : (
          <SegmentChartCard
            model={totalModel}
            segmentGraphModel={segmentGraphPartial ?? null}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.ITEM_COLLECTION_SEGMENT)}
          />
        ),
      });
    }
    if (showCustomerCategory) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <SaleByCustomerCategoryChartCard
            model={saleByCustomerCategoryModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY)}
          />
        ),
      });
    }
    if (showCustomerTrend) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.CUSTOMER_TREND,
        node: customerGraphsLoading ? (
          <DashboardChartCardSkeleton />
        ) : (
          <CustomerTrendChartCard
            model={customerTrendModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.CUSTOMER_TREND)}
          />
        ),
      });
    }
    return items;
  }, [
    showSale,
    showTotal,
    showTopProduct,
    showSegment,
    showCustomerCategory,
    showCustomerTrend,
    startDate,
    endDate,
    saleModel,
    totalModel,
    segmentGraphPartial,
    saleByCustomerCategoryModel,
    customerTrendModel,
    handleGroupByChange,
    onRemoveInlineGraph,
    chartLoadingMap,
    segmentSlotLoading,
    customerGraphsLoading,
  ]);

  const flowSlotsAfterRow3 = useMemo(() => {
    /** @type {{ id: string; node: React.ReactNode }[]} */
    const items = [];
    if (showSalespersonPerformance) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <SalespersonPerformanceChartCard
            model={salespersonPerformanceModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE)}
          />
        ),
      });
    }
    if (showPaymentMethod) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <PaymentMethodChartCard
            model={paymentMethodModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD)}
          />
        ),
      });
    }
    if (showCreditNote) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <CreditNoteChartCard
            model={creditNoteModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE)}
          />
        ),
      });
    }
    if (showDebitNote) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <DebitNoteChartCard
            model={debitNoteModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE)}
          />
        ),
      });
    }
    if (showCashRegister) {
      items.push({
        id: "cash_register_expected_actual",
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.CASH_REGISTER) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <CashRegisterExpectedActualChartCard
            model={cashRegisterModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.CASH_REGISTER)}
          />
        ),
      });
    }
    if (showDeposit) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.DEPOSIT,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.DEPOSIT) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <DepositChartCard
            model={depositModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.DEPOSIT)}
          />
        ),
      });
    }
    if (showCustomOrder) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <CustomOrderChartCard
            model={customOrderModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER)}
          />
        ),
      });
    }
    if (showRepairOrder) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <RepairOrderChartCard
            model={repairOrderModel}
            onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER)}
          />
        ),
      });
    }
    if (showRefund) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.REFUND,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.REFUND) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <RefundChartCard model={refundModel} onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.REFUND)} />
        ),
      });
    }
    if (showReserved) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.RESERVE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.RESERVE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <ReservedChartCard model={reservedModel} onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.RESERVE)} />
        ),
      });
    }
    if (showExchange) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.EXCHANGE,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.EXCHANGE) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <ExchangeChartCard model={exchangeModel} onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.EXCHANGE)} />
        ),
      });
    }
    if (showWishlist) {
      items.push({
        id: DASHBOARD_CHART_GRAPH_IDS.WISHLIST,
        node: isChartLoading(DASHBOARD_CHART_GRAPH_IDS.WISHLIST) ? (
          <DashboardChartCardSkeleton />
        ) : (
          <WishlistChartCard model={wishlistModel} onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.WISHLIST)} />
        ),
      });
    }
    return items;
  }, [
    showSalespersonPerformance,
    showPaymentMethod,
    showCreditNote,
    showDebitNote,
    showCustomOrder,
    showRepairOrder,
    showRefund,
    showReserved,
    showExchange,
    showWishlist,
    showDeposit,
    showCashRegister,
    salespersonPerformanceModel,
    paymentMethodModel,
    creditNoteModel,
    debitNoteModel,
    customOrderModel,
    repairOrderModel,
    refundModel,
    reservedModel,
    exchangeModel,
    wishlistModel,
    depositModel,
    cashRegisterModel,
    onRemoveInlineGraph,
    chartLoadingMap,
  ]);

  const flowRowsBeforeRow3 = useMemo(() => chunkIntoPairs(flowSlotsBeforeRow3), [flowSlotsBeforeRow3]);
  const flowRowsAfterRow3 = useMemo(() => chunkIntoPairs(flowSlotsAfterRow3), [flowSlotsAfterRow3]);

  if (
    !showSale &&
    !showTotal &&
    !showTopProduct &&
    !showSegment &&
    !showCustomerCategory &&
    !showCustomerTrend &&
    !showTopCustomer &&
    !showCustomerGroup &&
    !showAgeAndGender &&
    !showSalespersonPerformance &&
    !showPaymentMethod &&
    !showCreditNote &&
    !showDebitNote &&
    !showCustomOrder &&
    !showRepairOrder &&
    !showRefund &&
    !showReserved &&
    !showExchange &&
    !showWishlist &&
    !showDeposit &&
    !showCashRegister
  ) {
    return null;
  }

  return (
    <div className="pos-dashboard-charts-area" aria-label="Dashboard charts">
      {flowRowsBeforeRow3.map((pair, idx) => (
        <div
          key={`dash-flow-pre3-${idx}`}
          className={[
            "pos-dashboard-charts-area__grid",
            "pos-dashboard-charts-area__grid--flow",
            isCreditDebitPairRow(pair) ? "pos-dashboard-charts-area__grid--credit-debit-pair" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {pair.map((slot) => (
            <Fragment key={slot.id}>{slot.node}</Fragment>
          ))}
        </div>
      ))}
      {(showTopCustomer || showCustomerGroup || showAgeAndGender) && (
        <div className="pos-dashboard-charts-area__grid pos-dashboard-charts-area__grid--row3">
          {showTopCustomer ? (
            <TopCustomersChartCard
              startDate={startDate}
              endDate={endDate}
              onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.TOP_CUSTOMER)}
            />
          ) : null}
          {showCustomerGroup || showAgeAndGender ? (
            <div
              className={`pos-dashboard-charts-area__row3-right${
                row3RightSlotCount === 1 ? " pos-dashboard-charts-area__row3-right--single-slot" : ""
              }`}
            >
              {showCustomerGroup ? (
                customerGraphsLoading ? (
                  <DashboardChartCardSkeleton />
                ) : (
                  <CustomerGroupChartCard
                    model={customerGroupModel}
                    onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.CUSTOMER_GROUP)}
                  />
                )
              ) : null}
              {showAgeAndGender ? (
                customerGraphsLoading ? (
                  <DashboardChartCardSkeleton />
                ) : (
                  <AgeAndGenderChartCard
                    model={ageAndGenderModel}
                    onMenuRemove={() => onRemoveInlineGraph?.(DASHBOARD_CHART_GRAPH_IDS.AGE_AND_GENDER)}
                  />
                )
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {flowRowsAfterRow3.map((pair, idx) => (
        <div
          key={`dash-flow-post3-${idx}`}
          className={[
            "pos-dashboard-charts-area__grid",
            "pos-dashboard-charts-area__grid--flow",
            isCreditDebitPairRow(pair) ? "pos-dashboard-charts-area__grid--credit-debit-pair" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {pair.map((slot) => (
            <Fragment key={slot.id}>{slot.node}</Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

export default DashboardChartsArea;
